/**
 * True when `e` is a user- or client-aborted fetch (AbortController / $fetch / ofetch).
 * Use in `catch` to avoid surfacing “load failed” for intentional cancellation.
 */
/**
 * True when navigation left a start page or the associated fetch was aborted.
 * Use after `await` on create calls that accept an `AbortSignal`.
 */
export function isStartPageWorkCancelled(signal: AbortSignal, error?: unknown | null): boolean {
    return signal.aborted || isFetchAbortedError(error)
}

export function isFetchAbortedError(e: unknown): boolean {
    if (e == null) {
        return false
    }
    if (e instanceof DOMException && e.name === 'AbortError') {
        return true
    }
    if (e instanceof Error && e.name === 'AbortError') {
        return true
    }
    const o = e as { name?: string; cause?: { name?: string } }
    if (o.name === 'AbortError') {
        return true
    }
    if (o.cause?.name === 'AbortError') {
        return true
    }
    return false
}

/**
 * True when fetch fails on the client transport layer before an HTTP response exists.
 * Covers timeouts, connectivity drops, and WebView/browser "Load failed" style errors.
 */
export function isLikelyClientTransportError(e: unknown): boolean {
    if (e == null) return false
    if (isFetchAbortedError(e)) return true
    const o = e as { name?: unknown; message?: unknown; cause?: unknown }
    const cause = o.cause as { name?: unknown; message?: unknown } | undefined
    const text = [o.name, o.message, cause?.name, cause?.message]
        .filter((value): value is string => typeof value === 'string')
        .join(' ')
        .toLowerCase()

    return (
        text.includes('timeout') ||
        text.includes('timed out') ||
        text.includes('load failed') ||
        text.includes('failed to fetch') ||
        text.includes('network request failed') ||
        text.includes('<no response>')
    )
}
